import { useCallback, useEffect, useMemo, useState } from "react";
import { getVerificationHistory } from "../network";
import { THistoryDisplayResponse, THistoryResponse } from "../models";
import {
  buildHistoryDisplayResponse,
  getHistoryPaginationSince,
} from "../utils/historyDisplay";

export const useVerificationHistory = (
  verification_request_id?: string,
  initialLimit: number = 10
): [
  THistoryDisplayResponse | null,
  boolean,
  Error | null,
  () => void,
  () => void
] => {
  const [rawResult, setRawResult] = useState<THistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [autoLoadStoppedAfterEmpty, setAutoLoadStoppedAfterEmpty] =
    useState<boolean>(false);

  const displayResult = useMemo(
    () => (rawResult ? buildHistoryDisplayResponse(rawResult) : null),
    [rawResult],
  );

  const request = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setAutoLoadStoppedAfterEmpty(false);

    if (verification_request_id) {
      try {
        const response = await getVerificationHistory(
          verification_request_id,
          initialLimit,
        );
        setRawResult(response);
      } catch (e) {
        if (e instanceof Error) {
          setError(e);
        } else {
          setError(new Error('Unknown error'));
        }
      }
    } else {
      setError(new Error('Verification request ID is not provided'));
    }

    setIsLoading(false);
  }, [verification_request_id, initialLimit]);

  const loadMore = useCallback(async () => {
    if (!verification_request_id || !rawResult || !rawResult.hasMore || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getVerificationHistory(
        verification_request_id,
        initialLimit,
        getHistoryPaginationSince(rawResult),
      );

      if (response.history.length === 0) {
        setAutoLoadStoppedAfterEmpty(true);
      }

      setRawResult({
        history: [...rawResult.history, ...response.history],
        hasMore: response.hasMore,
      });
    } catch (e) {
      if (e instanceof Error) {
        setError(e);
      } else {
        setError(new Error('Unknown error'));
      }
    }

    setIsLoading(false);
  }, [verification_request_id, rawResult, isLoading, initialLimit]);

  useEffect(() => {
    if (verification_request_id) {
      request();
    }
  }, [request, verification_request_id]);

  useEffect(() => {
    if (
      !rawResult ||
      !displayResult ||
      isLoading ||
      autoLoadStoppedAfterEmpty ||
      !rawResult.hasMore ||
      displayResult.history.length >= initialLimit
    ) {
      return;
    }

    loadMore();
  }, [
    autoLoadStoppedAfterEmpty,
    displayResult,
    initialLimit,
    isLoading,
    loadMore,
    rawResult,
  ]);

  return [displayResult, isLoading, error, request, loadMore];
};
